import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabaseClient";
import { getSupabaseErrorMessage } from "./adminConfig";

const PAGE_SIZE = 75;

function clean(value) {
  return String(value ?? "").trim();
}

function releaseLabel(release) {
  if (!release) return "Unknown release";
  const years =
    release.year_end && Number(release.year_end) !== Number(release.year)
      ? `${release.year}-${release.year_end}`
      : release.year;
  return [years, release.brand, release.set_name || "Base / Flagship"]
    .filter(Boolean)
    .join(" · ");
}

function itemLabel(item) {
  return [
    item.card_number ? `#${item.card_number}` : null,
    item.name,
    item.subset ? `(${item.subset})` : null,
  ]
    .filter(Boolean)
    .join(" ");
}

export default function ReleaseManager({ currentUser }) {
  const [releases, setReleases] = useState([]);
  const [releaseSearch, setReleaseSearch] = useState("");
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [rules, setRules] = useState([]);
  const [selectedItemIds, setSelectedItemIds] = useState(() => new Set());

  const [loadingReleases, setLoadingReleases] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  const [editForm, setEditForm] = useState({
    year: "",
    year_end: "",
    brand: "",
    set_name: "",
    release_family_id: "",
    display_release_name: false,
  });
  const [subsetForm, setSubsetForm] = useState({ oldName: "", newName: "" });

  const [targetSearch, setTargetSearch] = useState("");
  const [targetReleaseId, setTargetReleaseId] = useState("");
  const [itemSearch, setItemSearch] = useState("");

  const [showCreateRelease, setShowCreateRelease] = useState(false);
  const [families, setFamilies] = useState([]);
  const [familySearch, setFamilySearch] = useState("");
  const [selectedFamilyId, setSelectedFamilyId] = useState("");
  const [showCreateFamily, setShowCreateFamily] = useState(false);
  const [familyForm, setFamilyForm] = useState({ family_key: "", name: "" });
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [createForm, setCreateForm] = useState({
    category_id: "",
    subcategory_id: "",
    year: "",
    year_end: "",
    brand: "",
    set_name: "",
    release_family_id: "",
    display_release_name: false,
    is_multi_sport: false,
  });
  const [showSubsetAssign, setShowSubsetAssign] = useState(false);
  const [subsetAssignName, setSubsetAssignName] = useState("");

  const [showVariantApply, setShowVariantApply] = useState(false);
  const [variantSearch, setVariantSearch] = useState("");
  const [variantOptions, setVariantOptions] = useState([]);
  const [selectedVariantId, setSelectedVariantId] = useState("");
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [variantMeta, setVariantMeta] = useState({});
  const [groupMembers, setGroupMembers] = useState([]);
  const [showApplicabilityAdd, setShowApplicabilityAdd] = useState(false);
  const [applicabilityScope, setApplicabilityScope] = useState("release");
  const [applicabilitySubset, setApplicabilitySubset] = useState("");
  const [editingGroupId, setEditingGroupId] = useState("");
  const [selectedGroupMemberIds, setSelectedGroupMemberIds] = useState(() => new Set());
  const [managingVariantId, setManagingVariantId] = useState("");
  const [managedVariantItemIds, setManagedVariantItemIds] = useState(() => new Set());
  const [managedVariantSearch, setManagedVariantSearch] = useState("");

  const fetchReleases = useCallback(async () => {
    setLoadingReleases(true);
    setErrorMessage("");

    const { data, error } = await supabase
      .from("card_releases")
      .select(
        "id, category_id, subcategory_id, year, year_end, brand, set_name, is_multi_sport, release_family_id, display_release_name, created_at, updated_at"
      )
      .order("year", { ascending: false })
      .order("brand", { ascending: true })
      .limit(5000);

    if (error) {
      setErrorMessage(
        getSupabaseErrorMessage(error, "Unable to load canonical releases.")
      );
      setReleases([]);
    } else {
      setReleases(data || []);
    }

    setLoadingReleases(false);
  }, []);

  useEffect(() => {
    fetchReleases();
  }, [fetchReleases]);

  const fetchFamilies = useCallback(async () => {
    const { data, error } = await supabase.rpc("admin_list_release_families");
    if (error) {
      setErrorMessage(getSupabaseErrorMessage(error, "Unable to load release families."));
      setFamilies([]);
      return;
    }
    setFamilies(data || []);
  }, []);

  useEffect(() => {
    fetchFamilies();
  }, [fetchFamilies]);

  useEffect(() => {
    let cancelled = false;

    async function fetchTaxonomy() {
      const [categoryResult, subcategoryResult] = await Promise.all([
        supabase.from("categories").select("id, name").order("name"),
        supabase
          .from("subcategories")
          .select("id, name, category_id")
          .order("name"),
      ]);

      if (cancelled) return;

      if (categoryResult.error || subcategoryResult.error) {
        const error = categoryResult.error || subcategoryResult.error;
        setErrorMessage(
          getSupabaseErrorMessage(error, "Unable to load release taxonomy.")
        );
        return;
      }

      const nextCategories = categoryResult.data || [];
      setCategories(nextCategories);
      setSubcategories(subcategoryResult.data || []);

      const sportsCard =
        nextCategories.find(
          (category) => clean(category.name).toLowerCase() === "sports card"
        ) || nextCategories[0];

      if (sportsCard) {
        setCreateForm((prev) =>
          prev.category_id
            ? prev
            : { ...prev, category_id: String(sportsCard.id) }
        );
      }
    }

    fetchTaxonomy();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadRelease = useCallback(async (release) => {
    if (!release?.id) return;

    setSelectedRelease(release);
    setEditForm({
      year: release.year ?? "",
      year_end: release.year_end ?? "",
      brand: release.brand ?? "",
      set_name: release.set_name ?? "",
      release_family_id: release.release_family_id ? String(release.release_family_id) : "",
      display_release_name: Boolean(release.display_release_name),
    });
    setSubsetForm({ oldName: "", newName: "" });
    setSelectedItemIds(new Set());
    setItemSearch("");
    setTargetReleaseId("");
    setTargetSearch("");
    setErrorMessage("");
    setStatusMessage("");
    setEditingGroupId("");
    setSelectedGroupMemberIds(new Set());
    setManagingVariantId("");
    setManagedVariantItemIds(new Set());
    setManagedVariantSearch("");
    setLoadingDetails(true);

    const [itemsResult, groupsResult, rulesResult, membersResult] = await Promise.all([
      supabase
        .from("items")
        .select(
          "id, name, card_number, subset, subcategory_id, year, year_end, brand, set_name"
        )
        .eq("release_id", release.id)
        .order("card_number", { ascending: true })
        .limit(10000),
      supabase
        .from("card_item_groups")
        .select("id, group_key, name, description, purpose, managed_variant_id")
        .eq("release_id", release.id)
        .order("name", { ascending: true }),
      supabase
        .from("variant_applicability")
        .select("id, variant_id, scope_type, mode, subset_name, group_id, item_id")
        .eq("release_id", release.id)
        .order("scope_type", { ascending: true }),
      supabase
        .from("card_item_group_members")
        .select("group_id, item_id, release_id")
        .eq("release_id", release.id),
    ]);

    const firstError =
      itemsResult.error || groupsResult.error || rulesResult.error || membersResult.error;

    if (firstError) {
      setErrorMessage(
        getSupabaseErrorMessage(
          firstError,
          "Unable to load all release details."
        )
      );
    }

    const nextRules = rulesResult.data || [];
    const variantIds = [...new Set(nextRules.map((rule) => rule.variant_id).filter(Boolean))];
    let nextVariantMeta = {};
    if (variantIds.length) {
      const { data: variantRows, error: variantError } = await supabase
        .from("variants")
        .select("id, name, category")
        .in("id", variantIds);
      if (variantError) {
        setErrorMessage(
          getSupabaseErrorMessage(variantError, "Release loaded, but variant names could not be resolved.")
        );
      } else {
        nextVariantMeta = Object.fromEntries((variantRows || []).map((variant) => [String(variant.id), variant]));
      }
    }

    setItems(itemsResult.data || []);
    setGroups(groupsResult.data || []);
    setRules(nextRules);
    setGroupMembers(membersResult.data || []);
    setVariantMeta(nextVariantMeta);
    setLoadingDetails(false);
  }, []);

  const refreshSelected = useCallback(async () => {
    if (!selectedRelease?.id) return;
    const { data, error } = await supabase
      .from("card_releases")
      .select(
        "id, category_id, subcategory_id, year, year_end, brand, set_name, is_multi_sport, release_family_id, display_release_name, created_at, updated_at"
      )
      .eq("id", selectedRelease.id)
      .single();

    await fetchReleases();

    if (error || !data) {
      setSelectedRelease(null);
      setItems([]);
      setGroups([]);
      setRules([]);
      setGroupMembers([]);
      setVariantMeta({});
      return;
    }

    await loadRelease(data);
  }, [fetchReleases, loadRelease, selectedRelease?.id]);

  const filteredReleases = useMemo(() => {
    const q = releaseSearch.toLowerCase().trim();
    if (!q) return releases.slice(0, PAGE_SIZE);

    return releases
      .filter((release) => {
        const haystack = [
          release.id,
          release.year,
          release.year_end,
          release.brand,
          release.set_name,
          release.release_family_id,
          families.find((f) => String(f.id) === String(release.release_family_id))?.family_key,
          families.find((f) => String(f.id) === String(release.release_family_id))?.name,
          release.is_multi_sport ? "multi sport multisport" : "single sport",
        ]
          .filter((v) => v !== null && v !== undefined)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      })
      .slice(0, PAGE_SIZE);
  }, [releaseSearch, releases, families]);

  const targetCandidates = useMemo(() => {
    const q = targetSearch.toLowerCase().trim();
    if (!q || !selectedRelease) return [];

    return releases
      .filter((release) => release.id !== selectedRelease.id)
      .filter((release) => {
        const haystack = [
          release.id,
          release.year,
          release.year_end,
          release.brand,
          release.set_name,
          release.is_multi_sport ? "multi sport multisport" : "single sport",
        ]
          .filter((v) => v !== null && v !== undefined)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      })
      .slice(0, 20);
  }, [releases, selectedRelease, targetSearch]);

  const targetRelease = useMemo(
    () =>
      releases.find(
        (release) => String(release.id) === String(targetReleaseId)
      ) || null,
    [releases, targetReleaseId]
  );

  const createSubcategories = useMemo(
    () =>
      subcategories.filter(
        (subcategory) =>
          String(subcategory.category_id) === String(createForm.category_id)
      ),
    [createForm.category_id, subcategories]
  );

  const subsets = useMemo(() => {
    const counts = new Map();
    items.forEach((item) => {
      const name = clean(item.subset);
      if (!name) return;
      counts.set(name, (counts.get(name) || 0) + 1);
    });
    return [...counts.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  const scopeCounts = useMemo(() => {
    const next = { release: 0, subset: 0, group: 0, item: 0 };
    rules.forEach((rule) => {
      if (next[rule.scope_type] !== undefined) next[rule.scope_type] += 1;
    });
    return next;
  }, [rules]);

  const visibleItems = useMemo(() => {
    const q = itemSearch.toLowerCase().trim();
    if (!q) return items;
    return items.filter((item) =>
      [item.id, item.card_number, item.name, item.subset]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [itemSearch, items]);

  const selectedItems = useMemo(
    () =>
      items.filter((item) =>
        selectedItemIds.has(String(item.id))
      ),
    [items, selectedItemIds]
  );

  const selectedSubsetSummary = useMemo(() => {
    if (!selectedItems.length) return "No cards selected";

    const names = [
      ...new Set(
        selectedItems.map((item) => clean(item.subset) || "No subset")
      ),
    ];

    return names.length === 1 ? names[0] : "Multiple subsets";
  }, [selectedItems]);

  const toggleItem = (itemId) => {
    const key = String(itemId);
    setSelectedItemIds((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectVisible = () => {
    setSelectedItemIds(
      new Set(visibleItems.map((item) => String(item.id)))
    );
  };

  const clearSelected = () => setSelectedItemIds(new Set());

  const assignSelectedItemsToSubset = async () => {
    if (!selectedRelease || selectedItems.length === 0) {
      setErrorMessage("Select one or more canonical cards first.");
      return;
    }

    const subsetName = clean(subsetAssignName);
    if (!subsetName) {
      setErrorMessage("Enter or choose a subset name.");
      return;
    }

    if (
      !window.confirm(
        `Assign ${selectedItems.length} selected card${selectedItems.length === 1 ? "" : "s"} to subset "${subsetName}"?\n\nCurrent selection: ${selectedSubsetSummary}`
      )
    ) return;

    setBusy(true);
    setErrorMessage("");
    setStatusMessage("");

    const { data, error } = await supabase.rpc(
      "admin_assign_items_to_subset",
      {
        p_release_id: String(selectedRelease.id),
        p_item_ids: selectedItems.map((item) => String(item.id)),
        p_subset_name: subsetName,
      }
    );

    if (error) {
      setErrorMessage(
        getSupabaseErrorMessage(error, "Unable to assign subset.")
      );
      setBusy(false);
      return;
    }

    setStatusMessage(
      `Assigned ${data?.items_changed ?? selectedItems.length} card(s) to "${subsetName}".`
    );
    setShowSubsetAssign(false);
    setSubsetAssignName("");
    setSelectedItemIds(new Set());
    await loadRelease(selectedRelease);
    setBusy(false);
  };

  const removeSelectedItemsFromSubset = async () => {
    if (!selectedRelease || selectedItems.length === 0) {
      setErrorMessage("Select one or more canonical cards first.");
      return;
    }

    if (
      !window.confirm(
        `Remove ${selectedItems.length} selected card${selectedItems.length === 1 ? "" : "s"} from their current subset?\n\nCurrent selection: ${selectedSubsetSummary}\n\nThe cards remain in this release.`
      )
    ) return;

    setBusy(true);
    setErrorMessage("");
    setStatusMessage("");

    const { data, error } = await supabase.rpc(
      "admin_assign_items_to_subset",
      {
        p_release_id: String(selectedRelease.id),
        p_item_ids: selectedItems.map((item) => String(item.id)),
        p_subset_name: null,
      }
    );

    if (error) {
      setErrorMessage(
        getSupabaseErrorMessage(error, "Unable to remove subset assignment.")
      );
      setBusy(false);
      return;
    }

    setStatusMessage(
      `Removed subset assignment from ${data?.items_changed ?? selectedItems.length} card(s).`
    );
    setSelectedItemIds(new Set());
    await loadRelease(selectedRelease);
    setBusy(false);
  };

  const openVariantApply = async () => {
    if (!selectedRelease || selectedItems.length === 0) {
      setErrorMessage("Select one or more canonical cards first.");
      return;
    }

    setShowVariantApply(true);
    setVariantSearch("");
    setSelectedVariantId("");
    setVariantOptions([]);
    setErrorMessage("");
    setStatusMessage("");
  };

  useEffect(() => {
    if (!showVariantApply && !showApplicabilityAdd) return undefined;

    const q = clean(variantSearch);
    if (q.length < 2) {
      setVariantOptions([]);
      setLoadingVariants(false);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setLoadingVariants(true);

      const escaped = q.replace(/[%_,]/g, "");
      const { data, error } = await supabase
        .from("variants")
        .select("id, name, category")
        .or(`name.ilike.%${escaped}%,category.ilike.%${escaped}%`)
        .order("name", { ascending: true })
        .limit(50);

      if (cancelled) return;

      if (error) {
        setErrorMessage(
          getSupabaseErrorMessage(error, "Unable to search canonical variants.")
        );
        setVariantOptions([]);
      } else {
        setVariantOptions(data || []);
      }
      setLoadingVariants(false);
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [showVariantApply, showApplicabilityAdd, variantSearch]);

  const applyVariantToSelectedItems = async () => {
    if (!selectedRelease || !selectedItems.length) {
      setErrorMessage("Select one or more canonical cards first.");
      return;
    }

    const variant = variantOptions.find(
      (option) => String(option.id) === String(selectedVariantId)
    );

    if (!variant) {
      setErrorMessage("Choose a canonical variant first.");
      return;
    }

    const preview = selectedItems
      .slice(0, 8)
      .map((item) => `• ${itemLabel(item)}`)
      .join("\n");
    const more =
      selectedItems.length > 8
        ? `\n• …and ${selectedItems.length - 8} more`
        : "";

    if (
      !window.confirm(
        `Apply canonical variant?\n\n${variant.name}${variant.category ? ` · ${variant.category}` : ""}\n\nTo ${selectedItems.length} selected card${selectedItems.length === 1 ? "" : "s"}:\n${preview}${more}\n\nHobbli will keep one-card coverage as an item rule and automatically use/reuse a managed group when multiple individual cards share this variant.`
      )
    ) return;

    setBusy(true);
    setErrorMessage("");
    setStatusMessage("");

    const { data, error } = await supabase.rpc(
      "admin_apply_variant_to_items",
      {
        p_release_id: String(selectedRelease.id),
        p_variant_id: String(variant.id),
        p_item_ids: selectedItems.map((item) => String(item.id)),
        p_source_note: "Applied from Release Manager",
      }
    );

    if (error) {
      setErrorMessage(
        getSupabaseErrorMessage(error, "Unable to apply variant to selected cards.")
      );
      setBusy(false);
      return;
    }

    const effectiveCount = Number(data?.effective_item_count || selectedItems.length);
    const scope = data?.scope_type || "item";
    const action = data?.action || "applied";

    setStatusMessage(
      `Applied ${variant.name} to the selected card${selectedItems.length === 1 ? "" : "s"}. ${action} · ${scope} scope · ${effectiveCount} effective card${effectiveCount === 1 ? "" : "s"}.`
    );
    setShowVariantApply(false);
    setVariantSearch("");
    setSelectedVariantId("");
    setVariantOptions([]);
    clearSelected();
    await loadRelease(selectedRelease);
    setBusy(false);
  };

  const openApplicabilityAdd = () => {
    setShowApplicabilityAdd(true);
    setApplicabilityScope(selectedItems.length ? "selected" : "release");
    setApplicabilitySubset("");
    setVariantSearch("");
    setSelectedVariantId("");
    setVariantOptions([]);
    setErrorMessage("");
    setStatusMessage("");
  };

  const applyApplicabilityRule = async () => {
    if (!selectedRelease) return;
    const variant = variantOptions.find(
      (option) => String(option.id) === String(selectedVariantId)
    );
    if (!variant) {
      setErrorMessage("Choose a canonical variant first.");
      return;
    }
    if (applicabilityScope === "subset" && !clean(applicabilitySubset)) {
      setErrorMessage("Choose a subset first.");
      return;
    }
    if (applicabilityScope === "selected" && !selectedItems.length) {
      setErrorMessage("Select one or more canonical cards first.");
      return;
    }

    const target =
      applicabilityScope === "release"
        ? `the entire ${releaseLabel(selectedRelease)} release (${items.length} cards)`
        : applicabilityScope === "subset"
          ? `the entire \"${applicabilitySubset}\" subset`
          : `${selectedItems.length} selected canonical card${selectedItems.length === 1 ? "" : "s"}`;

    if (!window.confirm(
      `Apply ${variant.name} to ${target}?\n\nThis creates canonical applicability knowledge. Broader rules do not erase more-specific include/exclude rules.`
    )) return;

    setBusy(true);
    setErrorMessage("");
    setStatusMessage("");

    let result;
    if (applicabilityScope === "selected") {
      result = await supabase.rpc("admin_apply_variant_to_items", {
        p_release_id: String(selectedRelease.id),
        p_variant_id: String(variant.id),
        p_item_ids: selectedItems.map((item) => String(item.id)),
        p_source_note: "Applied from Release Manager applicability panel",
      });
    } else {
      result = await supabase.rpc("admin_apply_variant_scope", {
        p_release_id: String(selectedRelease.id),
        p_variant_id: String(variant.id),
        p_scope_type: applicabilityScope,
        p_subset_name: applicabilityScope === "subset" ? applicabilitySubset : null,
        p_source_note: "Applied from Release Manager applicability panel",
      });
    }

    if (result.error) {
      setErrorMessage(getSupabaseErrorMessage(result.error, "Unable to apply variant applicability."));
      setBusy(false);
      return;
    }

    setStatusMessage(`Applied ${variant.name} to ${target}.`);
    setShowApplicabilityAdd(false);
    setVariantSearch("");
    setSelectedVariantId("");
    setVariantOptions([]);
    clearSelected();
    await loadRelease(selectedRelease);
    setBusy(false);
  };

  const effectiveVariantItemIds = (variantId) => {
    const variantRules = rules.filter(
      (rule) => String(rule.variant_id) === String(variantId)
    );

    const result = new Set();

    items.forEach((item) => {
      const matches = [];

      variantRules.forEach((rule) => {
        if (rule.scope_type === "release") {
          matches.push({ rule, specificity: 1 });
          return;
        }

        if (
          rule.scope_type === "subset" &&
          clean(item.subset) &&
          clean(item.subset).toLowerCase() === clean(rule.subset_name).toLowerCase()
        ) {
          matches.push({ rule, specificity: 2 });
          return;
        }

        if (rule.scope_type === "group") {
          const isMember = groupMembers.some(
            (member) =>
              String(member.group_id) === String(rule.group_id) &&
              String(member.item_id) === String(item.id)
          );
          if (isMember) matches.push({ rule, specificity: 3 });
          return;
        }

        if (
          rule.scope_type === "item" &&
          String(rule.item_id) === String(item.id)
        ) {
          matches.push({ rule, specificity: 4 });
        }
      });

      if (!matches.length) return;
      const highest = Math.max(...matches.map((match) => match.specificity));
      const winners = matches.filter((match) => match.specificity === highest);
      const hasExclude = winners.some((match) => match.rule.mode === "exclude");
      const hasInclude = winners.some((match) => match.rule.mode === "include");

      if (hasInclude && !hasExclude) result.add(String(item.id));
    });

    return result;
  };

  const openManageVariantCards = (variantId) => {
    setManagingVariantId(String(variantId));
    setManagedVariantItemIds(effectiveVariantItemIds(variantId));
    setManagedVariantSearch("");
    setErrorMessage("");
    setStatusMessage("");
  };

  const saveManagedVariantCards = async () => {
    if (!selectedRelease || !managingVariantId) return;

    const variant = variantMeta[String(managingVariantId)];
    const name = variant?.name || `Variant #${managingVariantId}`;
    const selectedCount = managedVariantItemIds.size;

    if (!window.confirm(
      `Save exact applicability for ${name}?\n\n${selectedCount} of ${items.length} canonical cards will be marked applicable.\n\nHobbli will normalize the underlying release/subset/group/item rules automatically.`
    )) return;

    setBusy(true);
    setErrorMessage("");
    setStatusMessage("");

    const { data, error } = await supabase.rpc(
      "admin_set_variant_applicable_items",
      {
        p_release_id: String(selectedRelease.id),
        p_variant_id: String(managingVariantId),
        p_item_ids: [...managedVariantItemIds],
        p_source_note: "Managed from Release Manager card applicability editor",
      }
    );

    if (error) {
      setErrorMessage(
        getSupabaseErrorMessage(error, "Unable to save exact variant applicability.")
      );
      setBusy(false);
      return;
    }

    setStatusMessage(
      `Saved ${name}: ${selectedCount} of ${items.length} cards applicable · ${data?.action || "normalized"}.`
    );
    setManagingVariantId("");
    setManagedVariantItemIds(new Set());
    setManagedVariantSearch("");
    await loadRelease(selectedRelease);
    setBusy(false);
  };

  const removeApplicabilityRule = async (rule) => {
    const variant = variantMeta[String(rule.variant_id)];
    const name = variant?.name || `Variant #${rule.variant_id}`;
    if (!window.confirm(
      `Remove this applicability rule?\n\n${name}\n${rule.scope_type.toUpperCase()} · ${rule.mode.toUpperCase()}\n\nMore-specific or less-specific rules may still make this variant applicable to some cards.`
    )) return;

    setBusy(true);
    setErrorMessage("");
    setStatusMessage("");
    const { data, error } = await supabase.rpc("admin_remove_variant_applicability", {
      p_rule_id: String(rule.id),
    });
    if (error) {
      setErrorMessage(getSupabaseErrorMessage(error, "Unable to remove applicability rule."));
    } else {
      setStatusMessage(`Removed applicability rule for ${name}.${data?.removed_managed_group ? " Its managed card group was removed too." : ""}`);
      setEditingGroupId("");
      await loadRelease(selectedRelease);
    }
    setBusy(false);
  };

  const removeSelectedGroupMembers = async () => {
    if (!editingGroupId || !selectedGroupMemberIds.size) return;
    const group = groups.find((entry) => String(entry.id) === String(editingGroupId));
    if (!group) return;
    if (!window.confirm(
      `Remove ${selectedGroupMemberIds.size} selected card${selectedGroupMemberIds.size === 1 ? "" : "s"} from ${group.name}?\n\nIf one card remains, Hobbli will normalize the group back to an item rule. If none remain, the applicability will be removed.`
    )) return;

    setBusy(true);
    setErrorMessage("");
    setStatusMessage("");
    const { data, error } = await supabase.rpc("admin_remove_items_from_variant_group", {
      p_group_id: String(group.id),
      p_item_ids: [...selectedGroupMemberIds],
    });
    if (error) {
      setErrorMessage(getSupabaseErrorMessage(error, "Unable to remove cards from the managed variant group."));
    } else {
      setStatusMessage(`Updated ${group.name}: ${data?.action || "updated"}.`);
      setEditingGroupId("");
      setSelectedGroupMemberIds(new Set());
      await loadRelease(selectedRelease);
    }
    setBusy(false);
  };

  const filteredFamilies = useMemo(() => {
    const q = clean(familySearch).toLowerCase();
    if (!q) return families.slice(0, 50);
    return families.filter((family) =>
      [family.id, family.family_key, family.name, family.year, family.brand]
        .filter(Boolean).join(" ").toLowerCase().includes(q)
    ).slice(0, 50);
  }, [families, familySearch]);

  const createFamily = async () => {
    const familyKey = clean(familyForm.family_key).toLowerCase();
    const name = clean(familyForm.name);
    const year = Number(createForm.year || editForm.year);
    const brand = clean(createForm.brand || editForm.brand);
    const categoryId = clean(createForm.category_id || selectedRelease?.category_id);
    const subcategoryId = clean(createForm.subcategory_id || selectedRelease?.subcategory_id);
    const isMultiSport = Boolean(createForm.is_multi_sport || selectedRelease?.is_multi_sport);
    if (!familyKey || !name || !Number.isInteger(year) || !brand || !categoryId) {
      setErrorMessage("Family key, family name, year, brand, and category are required.");
      return;
    }
    setBusy(true); setErrorMessage(""); setStatusMessage("");
    const { data, error } = await supabase.rpc("admin_create_release_family", {
      p_family_key: familyKey, p_name: name, p_category_id: categoryId,
      p_subcategory_id: isMultiSport ? null : (subcategoryId || null),
      p_year: year, p_year_end: clean(createForm.year_end || editForm.year_end) ? Number(createForm.year_end || editForm.year_end) : null,
      p_brand: brand, p_is_multi_sport: isMultiSport,
    });
    if (error) setErrorMessage(getSupabaseErrorMessage(error, "Unable to create release family."));
    else {
      const familyId = String(data?.family_id || data?.id || "");
      setStatusMessage(data?.created === false ? `Reused family #${familyId}.` : `Created family #${familyId}.`);
      setSelectedFamilyId(familyId);
      setCreateForm((prev) => ({ ...prev, release_family_id: familyId }));
      setEditForm((prev) => ({ ...prev, release_family_id: familyId }));
      setShowCreateFamily(false); setFamilyForm({ family_key: "", name: "" });
      await fetchFamilies();
    }
    setBusy(false);
  };

  const createRelease = async () => {
    const categoryId = clean(createForm.category_id);
    const subcategoryId = clean(createForm.subcategory_id);
    const year = Number(createForm.year);
    const yearEnd = clean(createForm.year_end)
      ? Number(createForm.year_end)
      : null;
    const brand = clean(createForm.brand);
    const setName = clean(createForm.set_name) || null;
    const isMultiSport = Boolean(createForm.is_multi_sport);
    const releaseFamilyId = clean(createForm.release_family_id) || null;
    const displayReleaseName = Boolean(createForm.display_release_name);

    if (!categoryId || !Number.isInteger(year) || !brand) {
      setErrorMessage("Category, year, and brand are required.");
      return;
    }

    if (!isMultiSport && !subcategoryId) {
      setErrorMessage("Choose a sport/subcategory for a single-sport release.");
      return;
    }

    if (yearEnd !== null && (!Number.isInteger(yearEnd) || yearEnd < year)) {
      setErrorMessage("Year end must be blank or greater than/equal to year.");
      return;
    }

    const categoryName =
      categories.find((category) => String(category.id) === categoryId)?.name ||
      "Selected category";
    const subcategoryName = isMultiSport
      ? "Multiple sports"
      : subcategories.find(
          (subcategory) => String(subcategory.id) === subcategoryId
        )?.name || "Selected sport";

    if (
      !window.confirm(
        `Create canonical release?\n\n${yearEnd && yearEnd !== year ? `${year}-${yearEnd}` : year} · ${brand} · ${setName || "Base / Flagship"}\n${categoryName} · ${subcategoryName}\n\nIf an identical release already exists, Hobbli will reuse it rather than create a duplicate.`
      )
    ) return;

    setBusy(true);
    setErrorMessage("");
    setStatusMessage("");

    const { data, error } = await supabase.rpc("admin_create_card_release", {
      p_category_id: categoryId,
      p_subcategory_id: isMultiSport ? null : subcategoryId,
      p_year: year,
      p_year_end: yearEnd,
      p_brand: brand,
      p_set_name: setName,
      p_is_multi_sport: isMultiSport,
    });

    if (error) {
      setErrorMessage(
        getSupabaseErrorMessage(error, "Unable to create release.")
      );
      setBusy(false);
      return;
    }

    const releaseId = data?.release_id;
    const created = data?.created === true;
    if (releaseFamilyId) {
      const { error: familyError } = await supabase.rpc("admin_set_release_family", {
        p_release_id: String(releaseId),
        p_release_family_id: String(releaseFamilyId),
        p_display_release_name: displayReleaseName,
      });
      if (familyError) {
        setErrorMessage(getSupabaseErrorMessage(familyError, "Release was created, but family assignment failed."));
        setBusy(false); return;
      }
    }
    setStatusMessage(
      created
        ? `Created release #${releaseId}.`
        : `That release already existed. Opened release #${releaseId} instead.`
    );

    setShowCreateRelease(false);
    setCreateForm((prev) => ({
      ...prev,
      subcategory_id: "",
      year: "",
      year_end: "",
      brand: "",
      set_name: "",
      release_family_id: "",
      display_release_name: false,
      is_multi_sport: false,
    }));

    const { data: refreshed, error: refreshError } = await supabase
      .from("card_releases")
      .select(
        "id, category_id, subcategory_id, year, year_end, brand, set_name, is_multi_sport, release_family_id, display_release_name, created_at, updated_at"
      )
      .eq("id", releaseId)
      .single();

    await fetchReleases();

    if (refreshError || !refreshed) {
      setErrorMessage(
        getSupabaseErrorMessage(
          refreshError,
          "Release was created, but could not be reopened automatically."
        )
      );
    } else {
      await loadRelease(refreshed);
    }

    setBusy(false);
  };

  const saveReleaseIdentity = async () => {
    if (!selectedRelease) return;
    const year = Number(editForm.year);
    const yearEnd = clean(editForm.year_end)
      ? Number(editForm.year_end)
      : null;
    const brand = clean(editForm.brand);
    const setName = clean(editForm.set_name) || null;
    const releaseFamilyId = clean(editForm.release_family_id) || null;
    const displayReleaseName = Boolean(editForm.display_release_name);

    if (!Number.isInteger(year) || !brand) {
      setErrorMessage("Release year and brand are required.");
      return;
    }
    if (yearEnd !== null && (!Number.isInteger(yearEnd) || yearEnd < year)) {
      setErrorMessage("Year end must be blank or greater than/equal to year.");
      return;
    }

    const before = releaseLabel(selectedRelease);
    const after = [
      yearEnd && yearEnd !== year ? `${year}-${yearEnd}` : year,
      brand,
      setName || "Base / Flagship",
    ].join(" · ");

    if (
      !window.confirm(
        `Update release #${selectedRelease.id}?\n\n${before}\n→ ${after}\n\nAll canonical items in this release will follow the release identity.`
      )
    ) return;

    setBusy(true);
    setErrorMessage("");
    setStatusMessage("");

    const { data, error } = await supabase.rpc("admin_update_card_release", {
      p_release_id: selectedRelease.id,
      p_year: year,
      p_year_end: yearEnd,
      p_brand: brand,
      p_set_name: setName,
    });

    if (error) {
      setErrorMessage(
        getSupabaseErrorMessage(error, "Unable to update release.")
      );
    } else {
      const { error: familyError } = await supabase.rpc("admin_set_release_family", {
        p_release_id: String(selectedRelease.id),
        p_release_family_id: releaseFamilyId ? String(releaseFamilyId) : null,
        p_display_release_name: displayReleaseName,
      });
      if (familyError) {
        setErrorMessage(getSupabaseErrorMessage(familyError, "Release identity saved, but family assignment failed."));
      } else {
        setStatusMessage(`Release #${selectedRelease.id} and family assignment updated successfully.`);
        console.log("admin_update_card_release:", data);
        await fetchFamilies();
        await refreshSelected();
      }
    }

    setBusy(false);
  };

  const renameSubset = async () => {
    if (!selectedRelease) return;
    const oldName = clean(subsetForm.oldName);
    const newName = clean(subsetForm.newName);

    if (!oldName || !newName) {
      setErrorMessage("Choose the existing subset and enter its new name.");
      return;
    }
    if (oldName.toLowerCase() === newName.toLowerCase()) {
      setErrorMessage("The new subset name must be different.");
      return;
    }

    const subsetCount =
      subsets.find(
        (subset) => subset.name.toLowerCase() === oldName.toLowerCase()
      )?.count || 0;
    const subsetRuleCount = rules.filter(
      (rule) =>
        rule.scope_type === "subset" &&
        clean(rule.subset_name).toLowerCase() === oldName.toLowerCase()
    ).length;

    if (
      !window.confirm(
        `Rename subset in release #${selectedRelease.id}?\n\n${oldName}\n→ ${newName}\n\n${subsetCount} canonical item(s) and ${subsetRuleCount} subset applicability rule(s) are in scope.`
      )
    ) return;

    setBusy(true);
    setErrorMessage("");
    setStatusMessage("");

    const { data, error } = await supabase.rpc(
      "admin_rename_release_subset",
      {
        p_release_id: selectedRelease.id,
        p_old_subset: oldName,
        p_new_subset: newName,
      }
    );

    if (error) {
      setErrorMessage(
        getSupabaseErrorMessage(error, "Unable to rename subset.")
      );
    } else {
      setStatusMessage(`Subset renamed from ${oldName} to ${newName}.`);
      console.log("admin_rename_release_subset:", data);
      await refreshSelected();
    }

    setBusy(false);
  };

  const moveSelectedItems = async () => {
    if (!selectedRelease || !targetRelease || !selectedItems.length) return;

    const preview = selectedItems
      .slice(0, 8)
      .map((item) => `• ${itemLabel(item)}`)
      .join("\n");
    const more =
      selectedItems.length > 8
        ? `\n• …and ${selectedItems.length - 8} more`
        : "";

    if (
      !window.confirm(
        `Move ${selectedItems.length} canonical item(s)?\n\nFROM:\n${releaseLabel(
          selectedRelease
        )} (#${selectedRelease.id})\n\nTO:\n${releaseLabel(targetRelease)} (#${
          targetRelease.id
        })\n\n${preview}${more}\n\nThe source release will NOT be deleted.`
      )
    ) return;

    setBusy(true);
    setErrorMessage("");
    setStatusMessage("");

    const { data, error } = await supabase.rpc(
      "admin_move_items_to_release",
      {
        p_item_ids: selectedItems.map((item) => String(item.id)),
        p_target_release_id: String(targetRelease.id),
      }
    );

    if (error) {
      setErrorMessage(
        getSupabaseErrorMessage(error, "Unable to move selected items.")
      );
    } else {
      const skipped = Number(data?.group_memberships_skipped || 0);
      setStatusMessage(
        `Moved ${data?.items_moved ?? selectedItems.length} item(s) to release #${
          targetRelease.id
        }.${
          skipped
            ? ` ${skipped} group membership(s) require manual review.`
            : ""
        }`
      );
      clearSelected();
      await refreshSelected();
    }

    setBusy(false);
  };

  const mergeIntoTarget = async () => {
    if (!selectedRelease || !targetRelease) return;

    if (
      !window.confirm(
        `MERGE RELEASE #${selectedRelease.id} INTO #${targetRelease.id}?\n\nSOURCE (will be removed if merge succeeds):\n${releaseLabel(
          selectedRelease
        )}\n${items.length} item(s), ${groups.length} group(s), ${rules.length} applicability rule(s)\n\nTARGET:\n${releaseLabel(
          targetRelease
        )}\n\nThis is a destructive catalog maintenance operation. The RPC will abort rather than silently violate structural constraints.`
      )
    ) return;

    const typed = window.prompt(
      `Type MERGE ${selectedRelease.id} to confirm this release merge.`
    );
    if (typed !== `MERGE ${selectedRelease.id}`) return;

    setBusy(true);
    setErrorMessage("");
    setStatusMessage("");

    const sourceId = selectedRelease.id;
    const { data, error } = await supabase.rpc(
      "admin_merge_card_releases",
      {
        p_source_release_id: String(sourceId),
        p_target_release_id: String(targetRelease.id),
      }
    );

    if (error) {
      setErrorMessage(
        getSupabaseErrorMessage(error, "Unable to merge releases.")
      );
    } else {
      setStatusMessage(
        `Release #${sourceId} merged into release #${targetRelease.id}.`
      );
      console.log("admin_merge_card_releases:", data);
      setSelectedRelease(null);
      setItems([]);
      setGroups([]);
      setRules([]);
      setSelectedItemIds(new Set());
      await fetchReleases();
      const { data: targetData } = await supabase
        .from("card_releases")
        .select(
          "id, category_id, subcategory_id, year, year_end, brand, set_name, is_multi_sport, release_family_id, display_release_name, created_at, updated_at"
        )
        .eq("id", targetRelease.id)
        .single();
      if (targetData) await loadRelease(targetData);
    }

    setBusy(false);
  };

  return (
    <div className="hrelease-manager">
      {errorMessage && <div className="hadmin-error">{errorMessage}</div>}
      {statusMessage && <div className="hadmin-success">{statusMessage}</div>}

      <div className="hrelease-layout">
        <section className="hadmin-panel hrelease-browser">
          <div className="hadmin-section-heading">
            <div>
              <h2>Releases</h2>
              <p>
                Search canonical releases by year, brand, set, release ID, or
                multi-sport status.
              </p>
            </div>
            <div className="hadmin-actions">
              <button
                type="button"
                className="hadmin-btn"
                onClick={() => {
                  setShowCreateRelease(true);
                  setErrorMessage("");
                  setStatusMessage("");
                }}
                disabled={busy}
              >
                + Create Release
              </button>
              <button
                type="button"
                className="hadmin-btn-secondary"
                onClick={fetchReleases}
                disabled={loadingReleases || busy}
              >
                Refresh
              </button>
            </div>
          </div>

          <input
            className="hadmin-input"
            value={releaseSearch}
            onChange={(event) => setReleaseSearch(event.target.value)}
            placeholder="Search releases… e.g. 1933 Goudey Sport Kings"
          />

          <div className="hrelease-list">
            {loadingReleases ? (
              <div className="hadmin-empty">Loading releases…</div>
            ) : filteredReleases.length ? (
              filteredReleases.map((release) => (
                <button
                  type="button"
                  key={release.id}
                  className={`hrelease-release-row ${
                    selectedRelease?.id === release.id ? "selected" : ""
                  }`}
                  onClick={() => loadRelease(release)}
                >
                  <div className="hrelease-release-main">
                    <strong>{releaseLabel(release)}</strong>
                    <span>Release #{release.id}{release.release_family_id ? ` · Family #${release.release_family_id}` : " · Unassigned family"}</span>
                  </div>
                  <span
                    className={`hadmin-pill ${
                      release.is_multi_sport ? "orange" : "blue"
                    }`}
                  >
                    {release.is_multi_sport ? "Multi-sport" : "Single-sport"}
                  </span>
                </button>
              ))
            ) : (
              <div className="hadmin-empty">No matching releases.</div>
            )}
          </div>

          {!releaseSearch && releases.length > PAGE_SIZE && (
            <div className="hadmin-muted hrelease-limit-note">
              Showing the first {PAGE_SIZE} releases. Search to narrow the list.
            </div>
          )}
        </section>

        <section className="hrelease-workspace">
          {!selectedRelease ? (
            <div className="hadmin-panel hadmin-empty hrelease-empty-workspace">
              Select a release to inspect and maintain it.
            </div>
          ) : (
            <>
              <div className="hadmin-panel">
                <div className="hadmin-section-heading">
                  <div>
                    <h2>{releaseLabel(selectedRelease)}</h2>
                    <p>
                      Release #{selectedRelease.id} ·{" "}
                      {selectedRelease.is_multi_sport
                        ? "Explicit multi-sport release"
                        : "Sport-specific release"}
                      {selectedRelease.release_family_id ? ` · Family #${selectedRelease.release_family_id}` : " · No family assigned"}
                    </p>
                  </div>
                  <span
                    className={`hadmin-pill ${
                      selectedRelease.is_multi_sport ? "orange" : "green"
                    }`}
                  >
                    {selectedRelease.is_multi_sport
                      ? "Multi-sport"
                      : "Single-sport"}
                  </span>
                </div>

                <div className="hrelease-stats">
                  <div className="hrelease-stat">
                    <span>Canonical items</span>
                    <strong>{items.length}</strong>
                  </div>
                  <div className="hrelease-stat">
                    <span>Subsets</span>
                    <strong>{subsets.length}</strong>
                  </div>
                  <div className="hrelease-stat">
                    <span>Groups</span>
                    <strong>{groups.length}</strong>
                  </div>
                  <div className="hrelease-stat">
                    <span>Variant rules</span>
                    <strong>{rules.length}</strong>
                  </div>
                </div>

                <div className="hrelease-scope-strip">
                  <span>Rules:</span>
                  <span className="hadmin-pill">Release {scopeCounts.release}</span>
                  <span className="hadmin-pill">Subset {scopeCounts.subset}</span>
                  <span className="hadmin-pill">Group {scopeCounts.group}</span>
                  <span className="hadmin-pill">Item {scopeCounts.item}</span>
                </div>
              </div>

              {loadingDetails ? (
                <div className="hadmin-panel hadmin-empty">
                  Loading release details…
                </div>
              ) : (
                <>
                  <div className="hrelease-two-col">
                    <div className="hadmin-panel">
                      <div className="hadmin-section-heading">
                        <div>
                          <h3>Edit Release Identity</h3>
                          <p>
                            Uses the guarded release-update RPC. Canonical items
                            follow the updated release identity.
                          </p>
                        </div>
                      </div>

                      <div className="hadmin-form-grid">
                        <div className="hadmin-form-field">
                          <label>Year</label>
                          <input
                            className="hadmin-input"
                            type="number"
                            value={editForm.year}
                            onChange={(event) =>
                              setEditForm((prev) => ({
                                ...prev,
                                year: event.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="hadmin-form-field">
                          <label>Year end</label>
                          <input
                            className="hadmin-input"
                            type="number"
                            value={editForm.year_end}
                            onChange={(event) =>
                              setEditForm((prev) => ({
                                ...prev,
                                year_end: event.target.value,
                              }))
                            }
                            placeholder="Optional"
                          />
                        </div>
                        <div className="hadmin-form-field">
                          <label>Brand</label>
                          <input
                            className="hadmin-input"
                            value={editForm.brand}
                            onChange={(event) =>
                              setEditForm((prev) => ({
                                ...prev,
                                brand: event.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="hadmin-form-field">
                          <label>Set name</label>
                          <input
                            className="hadmin-input"
                            value={editForm.set_name}
                            onChange={(event) =>
                              setEditForm((prev) => ({
                                ...prev,
                                set_name: event.target.value,
                              }))
                            }
                            placeholder="Optional"
                          />
                        </div>

                        <div className="hadmin-form-field full">
                          <label>Release family</label>
                          <select className="hadmin-select" value={editForm.release_family_id} onChange={(event) => setEditForm((prev) => ({ ...prev, release_family_id: event.target.value }))}>
                            <option value="">No family assigned</option>
                            {families.map((family) => <option key={family.id} value={family.id}>{family.year} · {family.brand} · {family.name} · {family.family_key} (#{family.id})</option>)}
                          </select>
                        </div>
                        <div className="hadmin-form-field full">
                          <label className="hrelease-check-label"><input type="checkbox" checked={Boolean(editForm.display_release_name)} onChange={(event) => setEditForm((prev) => ({ ...prev, display_release_name: event.target.checked }))} /> Show release name in user-facing set label</label>
                        </div>
                      </div>

                      <div className="hadmin-actions hrelease-action-row">
                        <button
                          type="button"
                          className="hadmin-btn"
                          onClick={saveReleaseIdentity}
                          disabled={busy}
                        >
                          Save Release Identity
                        </button>
                      </div>
                    </div>

                    <div className="hadmin-panel">
                      <div className="hadmin-section-heading">
                        <div>
                          <h3>Rename Subset</h3>
                          <p>
                            Renames the canonical subset and synchronized
                            subset-scoped applicability.
                          </p>
                        </div>
                      </div>

                      {subsets.length ? (
                        <>
                          <div className="hadmin-form-field">
                            <label>Existing subset</label>
                            <select
                              className="hadmin-select"
                              value={subsetForm.oldName}
                              onChange={(event) =>
                                setSubsetForm((prev) => ({
                                  ...prev,
                                  oldName: event.target.value,
                                }))
                              }
                            >
                              <option value="">Choose subset…</option>
                              {subsets.map((subset) => (
                                <option key={subset.name} value={subset.name}>
                                  {subset.name} ({subset.count})
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="hadmin-form-field hrelease-field-gap">
                            <label>New subset name</label>
                            <input
                              className="hadmin-input"
                              value={subsetForm.newName}
                              onChange={(event) =>
                                setSubsetForm((prev) => ({
                                  ...prev,
                                  newName: event.target.value,
                                }))
                              }
                              placeholder="New canonical subset name"
                            />
                          </div>

                          <div className="hadmin-actions hrelease-action-row">
                            <button
                              type="button"
                              className="hadmin-btn-secondary"
                              onClick={renameSubset}
                              disabled={busy}
                            >
                              Rename Subset
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="hadmin-empty">
                          No subsets yet — select canonical cards below and choose Assign Subset to create one.
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="hadmin-panel">
                    <div className="hadmin-section-heading">
                      <div>
                        <h3>Variant Applicability</h3>
                        <p>
                          Define which canonical variants can occur in this release. Use the narrowest verified scope supported by your source.
                        </p>
                      </div>
                      <button
                        type="button"
                        className="hadmin-btn"
                        onClick={openApplicabilityAdd}
                        disabled={busy}
                      >
                        + Add Variant Rule
                      </button>
                    </div>

                    {rules.length ? (
                      <div className="hadmin-table-wrap">
                        <table className="hadmin-table">
                          <thead>
                            <tr>
                              <th>Variant</th>
                              <th>Applies to</th>
                              <th>Coverage</th>
                              <th>Mode</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rules.map((rule) => {
                              const variant = variantMeta[String(rule.variant_id)];
                              const group = groups.find((entry) => String(entry.id) === String(rule.group_id));
                              const memberCount = rule.scope_type === "group"
                                ? groupMembers.filter((member) => String(member.group_id) === String(rule.group_id)).length
                                : rule.scope_type === "subset"
                                  ? items.filter((item) => clean(item.subset).toLowerCase() === clean(rule.subset_name).toLowerCase()).length
                                  : rule.scope_type === "release"
                                    ? items.length
                                    : 1;
                              const target = rule.scope_type === "release"
                                ? "Entire release"
                                : rule.scope_type === "subset"
                                  ? `Subset: ${rule.subset_name}`
                                  : rule.scope_type === "group"
                                    ? (group?.purpose === "variant" ? "Selected cards" : `Group: ${group?.name || rule.group_id}`)
                                    : `Card #${rule.item_id}`;
                              return (
                                <tr key={rule.id}>
                                  <td>
                                    <strong>{variant?.name || `Variant #${rule.variant_id}`}</strong>
                                    <div className="hadmin-muted">{variant?.category || `ID ${rule.variant_id}`}</div>
                                  </td>
                                  <td>{target}</td>
                                  <td>{memberCount} card{memberCount === 1 ? "" : "s"}</td>
                                  <td><span className={`hadmin-pill ${rule.mode === "exclude" ? "orange" : "green"}`}>{rule.mode}</span></td>
                                  <td>
                                    <div className="hadmin-actions">
                                      <button
                                        type="button"
                                        className="hadmin-btn-secondary"
                                        onClick={() => openManageVariantCards(rule.variant_id)}
                                        disabled={busy}
                                      >
                                        Manage Cards
                                      </button>
                                      {rule.scope_type === "group" && group?.purpose === "variant" && (
                                        <button
                                          type="button"
                                          className="hadmin-btn-secondary"
                                          onClick={() => {
                                            setEditingGroupId(String(group.id));
                                            setSelectedGroupMemberIds(new Set());
                                          }}
                                          disabled={busy}
                                        >
                                          View / Edit Cards
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        className="hadmin-btn-danger"
                                        onClick={() => removeApplicabilityRule(rule)}
                                        disabled={busy}
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="hadmin-empty">
                        No canonical variant applicability has been defined for this release yet.
                      </div>
                    )}

                    <div className="hadmin-info hrelease-create-note">
                      Specificity is Release → Subset → Group → Item. A more-specific rule wins; at equal specificity, EXCLUDE wins over INCLUDE.
                    </div>
                  </div>

                  <div className="hadmin-panel">
                    <div className="hadmin-section-heading">
                      <div>
                        <h3>Canonical Items</h3>
                        <p>
                          Select individual cards here when part of a release
                          belongs somewhere else.
                        </p>
                      </div>
                      <span className="hadmin-pill blue">
                        {selectedItemIds.size} selected
                      </span>
                    </div>

                    <div className="hrelease-subset-maintenance">
                      <div className="hrelease-subset-maintenance-copy">
                        <strong>Subset assignment</strong>
                        <span>
                          {selectedItems.length
                            ? `${selectedItems.length} selected · ${selectedSubsetSummary}`
                            : subsets.length
                              ? "Select canonical cards to assign or remove a subset."
                              : "No subsets yet — select canonical cards and choose Assign Subset to create one."}
                        </span>
                      </div>

                      <div className="hadmin-actions">
                        <button
                          type="button"
                          className="hadmin-btn"
                          disabled={!selectedItems.length || busy}
                          onClick={() => {
                            setSubsetAssignName(
                              selectedSubsetSummary !== "Multiple subsets" &&
                              selectedSubsetSummary !== "No subset" &&
                              selectedSubsetSummary !== "No cards selected"
                                ? selectedSubsetSummary
                                : ""
                            );
                            setShowSubsetAssign(true);
                            setErrorMessage("");
                            setStatusMessage("");
                          }}
                        >
                          Assign Subset
                        </button>

                        <button
                          type="button"
                          className="hadmin-btn-secondary"
                          disabled={
                            !selectedItems.length ||
                            busy ||
                            selectedItems.every((item) => !clean(item.subset))
                          }
                          onClick={removeSelectedItemsFromSubset}
                        >
                          Remove from Subset
                        </button>

                        <button
                          type="button"
                          className="hadmin-btn-secondary"
                          disabled={!selectedItems.length || busy}
                          onClick={openVariantApply}
                        >
                          Apply Variant
                        </button>
                      </div>
                    </div>

                    <div className="hadmin-toolbar">
                      <input
                        className="hadmin-input hadmin-search"
                        value={itemSearch}
                        onChange={(event) => setItemSearch(event.target.value)}
                        placeholder="Filter by card #, name, subset, or ID…"
                      />
                      <button
                        type="button"
                        className="hadmin-btn-secondary"
                        onClick={selectVisible}
                        disabled={!visibleItems.length}
                      >
                        Select Visible
                      </button>
                      <button
                        type="button"
                        className="hadmin-btn-secondary"
                        onClick={clearSelected}
                        disabled={!selectedItemIds.size}
                      >
                        Clear
                      </button>
                    </div>

                    <div className="hadmin-table-wrap hrelease-items-table-wrap">
                      <table className="hadmin-table hrelease-items-table">
                        <thead>
                          <tr>
                            <th></th>
                            <th>ID</th>
                            <th>Card #</th>
                            <th>Name</th>
                            <th>Subset</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleItems.map((item) => (
                            <tr
                              key={item.id}
                              className={
                                selectedItemIds.has(String(item.id))
                                  ? "selected"
                                  : ""
                              }
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedItemIds.has(String(item.id))}
                                  onChange={() => toggleItem(item.id)}
                                  aria-label={`Select ${itemLabel(item)}`}
                                />
                              </td>
                              <td>{item.id}</td>
                              <td>{item.card_number || "—"}</td>
                              <td>{item.name || "Unnamed"}</td>
                              <td>{item.subset || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="hadmin-panel">
                    <div className="hadmin-section-heading">
                      <div>
                        <h3>Move / Merge Target</h3>
                        <p>
                          Search for the destination release. Moving affects only
                          selected cards; merging consolidates the entire source
                          release.
                        </p>
                      </div>
                    </div>

                    <input
                      className="hadmin-input"
                      value={targetSearch}
                      onChange={(event) => {
                        setTargetSearch(event.target.value);
                        setTargetReleaseId("");
                      }}
                      placeholder="Search destination release…"
                    />

                    {targetSearch && !targetRelease && (
                      <div className="hrelease-target-results">
                        {targetCandidates.length ? (
                          targetCandidates.map((release) => (
                            <button
                              type="button"
                              key={release.id}
                              className="hrelease-target-row"
                              onClick={() => {
                                setTargetReleaseId(String(release.id));
                                setTargetSearch(releaseLabel(release));
                              }}
                            >
                              <span>
                                <strong>{releaseLabel(release)}</strong>
                                <small>Release #{release.id}</small>
                              </span>
                              <span
                                className={`hadmin-pill ${
                                  release.is_multi_sport ? "orange" : "blue"
                                }`}
                              >
                                {release.is_multi_sport
                                  ? "Multi-sport"
                                  : "Single-sport"}
                              </span>
                            </button>
                          ))
                        ) : (
                          <div className="hadmin-empty">
                            No matching destination releases.
                          </div>
                        )}
                      </div>
                    )}

                    {targetRelease && (
                      <div className="hrelease-target-selected">
                        <div>
                          <span>Destination release</span>
                          <strong>{releaseLabel(targetRelease)}</strong>
                          <small>Release #{targetRelease.id}</small>
                        </div>
                        <button
                          type="button"
                          className="hadmin-btn-secondary"
                          onClick={() => {
                            setTargetReleaseId("");
                            setTargetSearch("");
                          }}
                        >
                          Change
                        </button>
                      </div>
                    )}

                    <div className="hrelease-danger-actions">
                      <div>
                        <strong>Move selected canonical items</strong>
                        <p>
                          Moves {selectedItemIds.size} selected item(s). Source
                          release remains in place.
                        </p>
                        <button
                          type="button"
                          className="hadmin-btn"
                          onClick={moveSelectedItems}
                          disabled={
                            busy ||
                            !targetRelease ||
                            selectedItemIds.size === 0
                          }
                        >
                          Move {selectedItemIds.size || ""} Selected Item
                          {selectedItemIds.size === 1 ? "" : "s"}
                        </button>
                      </div>

                      <div className="hrelease-merge-zone">
                        <strong>Merge entire release</strong>
                        <p>
                          Consolidates this release into the destination and
                          removes the source release when the guarded RPC
                          succeeds.
                        </p>
                        <button
                          type="button"
                          className="hadmin-btn-danger"
                          onClick={mergeIntoTarget}
                          disabled={busy || !targetRelease}
                        >
                          Merge Release #{selectedRelease.id}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </section>
      </div>

      {showSubsetAssign && selectedRelease && (
        <div
          className="hrelease-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !busy) {
              setShowSubsetAssign(false);
            }
          }}
        >
          <div
            className="hadmin-panel hrelease-subset-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hrelease-subset-title"
          >
            <div className="hadmin-section-heading">
              <div>
                <h2 id="hrelease-subset-title">Assign Subset</h2>
                <p>
                  Assign the selected canonical cards to a new or existing
                  subset within release #{selectedRelease.id}.
                </p>
              </div>
              <button
                type="button"
                className="hadmin-icon-btn"
                onClick={() => setShowSubsetAssign(false)}
                disabled={busy}
                aria-label="Close assign subset"
              >
                ×
              </button>
            </div>

            <div className="hrelease-selection-summary">
              <strong>{selectedItems.length} card{selectedItems.length === 1 ? "" : "s"} selected</strong>
              <span>Current: {selectedSubsetSummary}</span>
            </div>

            <div className="hadmin-form-field">
              <label>Subset name</label>
              <input
                className="hadmin-input"
                list="hrelease-existing-subsets"
                value={subsetAssignName}
                onChange={(event) => setSubsetAssignName(event.target.value)}
                placeholder="Type a new subset or choose an existing one"
                autoFocus
              />
              <datalist id="hrelease-existing-subsets">
                {subsets.map((subset) => (
                  <option key={subset.name} value={subset.name} />
                ))}
              </datalist>
            </div>

            <div className="hadmin-info hrelease-create-note">
              A new name creates the subset by assigning these cards to it.
              Choosing an existing name moves/adds the selected cards to that
              subset. Canonical IDs and release membership are preserved.
            </div>

            <div className="hadmin-actions hrelease-create-actions">
              <button
                type="button"
                className="hadmin-btn-secondary"
                onClick={() => setShowSubsetAssign(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="hadmin-btn"
                onClick={assignSelectedItemsToSubset}
                disabled={busy || !clean(subsetAssignName)}
              >
                {busy ? "Assigning…" : "Assign Subset"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showApplicabilityAdd && selectedRelease && (
        <div
          className="hrelease-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !busy) setShowApplicabilityAdd(false);
          }}
        >
          <div className="hadmin-panel hrelease-subset-modal" role="dialog" aria-modal="true">
            <div className="hadmin-section-heading">
              <div>
                <h2>Add Variant Applicability</h2>
                <p>Choose an existing canonical variant and the narrowest verified scope.</p>
              </div>
              <button type="button" className="hadmin-icon-btn" onClick={() => setShowApplicabilityAdd(false)} disabled={busy}>×</button>
            </div>

            <div className="hadmin-form-field">
              <label>Applies to</label>
              <select className="hadmin-select" value={applicabilityScope} onChange={(event) => setApplicabilityScope(event.target.value)}>
                <option value="release">Entire release ({items.length} cards)</option>
                {subsets.length > 0 && <option value="subset">Entire subset</option>}
                <option value="selected" disabled={!selectedItems.length}>Selected cards ({selectedItems.length})</option>
              </select>
            </div>

            {applicabilityScope === "subset" && (
              <div className="hadmin-form-field hrelease-field-gap">
                <label>Subset</label>
                <select className="hadmin-select" value={applicabilitySubset} onChange={(event) => setApplicabilitySubset(event.target.value)}>
                  <option value="">Choose subset…</option>
                  {subsets.map((subset) => <option key={subset.name} value={subset.name}>{subset.name} ({subset.count})</option>)}
                </select>
              </div>
            )}

            <div className="hadmin-form-field hrelease-field-gap">
              <label>Search canonical variants</label>
              <input className="hadmin-input" value={variantSearch} onChange={(event) => { setVariantSearch(event.target.value); setSelectedVariantId(""); }} placeholder="e.g. Piedmont 350 Factory No.25" autoFocus />
            </div>

            {loadingVariants ? (
              <div className="hadmin-empty">Searching variants…</div>
            ) : clean(variantSearch).length >= 2 && variantOptions.length ? (
              <div className="hadmin-form-field hrelease-field-gap">
                <label>Canonical variant</label>
                <select className="hadmin-select" value={selectedVariantId} onChange={(event) => setSelectedVariantId(event.target.value)}>
                  <option value="">Choose variant…</option>
                  {variantOptions.map((variant) => <option key={variant.id} value={variant.id}>{variant.name}{variant.category ? ` · ${variant.category}` : ""} (#{variant.id})</option>)}
                </select>
              </div>
            ) : clean(variantSearch).length >= 2 ? (
              <div className="hadmin-empty">No matching canonical variants.</div>
            ) : (
              <div className="hadmin-muted hrelease-create-note">Type at least 2 characters to search.</div>
            )}

            <div className="hadmin-info hrelease-create-note">
              Entire release and subset are intentionally broad assertions. Selected cards automatically normalize between item and Hobbli-managed group scope.
            </div>

            <div className="hadmin-actions hrelease-create-actions">
              <button type="button" className="hadmin-btn-secondary" onClick={() => setShowApplicabilityAdd(false)} disabled={busy}>Cancel</button>
              <button type="button" className="hadmin-btn" onClick={applyApplicabilityRule} disabled={busy || !selectedVariantId || (applicabilityScope === "subset" && !applicabilitySubset) || (applicabilityScope === "selected" && !selectedItems.length)}>{busy ? "Applying…" : "Apply Variant Rule"}</button>
            </div>
          </div>
        </div>
      )}

      {managingVariantId && selectedRelease && (() => {
        const variant = variantMeta[String(managingVariantId)];
        const search = clean(managedVariantSearch).toLowerCase();
        const visible = items.filter((item) => {
          if (!search) return true;
          return [item.id, item.card_number, item.name, item.subset]
            .some((value) => clean(value).toLowerCase().includes(search));
        });
        const visibleKeys = visible.map((item) => String(item.id));
        const allVisibleChecked = visibleKeys.length > 0 && visibleKeys.every((key) => managedVariantItemIds.has(key));
        const applicableCount = managedVariantItemIds.size;
        const excludedCount = Math.max(0, items.length - applicableCount);

        return (
          <div
            className="hrelease-modal-backdrop"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !busy) setManagingVariantId("");
            }}
          >
            <div className="hadmin-panel hrelease-subset-modal" role="dialog" aria-modal="true">
              <div className="hadmin-section-heading">
                <div>
                  <h2>Manage Variant Cards</h2>
                  <p>
                    {variant?.name || `Variant #${managingVariantId}`} · {applicableCount} of {items.length} cards applicable
                  </p>
                </div>
                <button type="button" className="hadmin-icon-btn" onClick={() => setManagingVariantId("")} disabled={busy}>×</button>
              </div>

              <div className="hadmin-info" style={{ marginBottom: 12 }}>
                Check every canonical card that can have this variant. Hobbli will choose the cleanest release, subset, group, item, and exception rules when you save.
              </div>

              <div className="hadmin-toolbar">
                <input
                  className="hadmin-input hadmin-search"
                  value={managedVariantSearch}
                  onChange={(event) => setManagedVariantSearch(event.target.value)}
                  placeholder="Filter by card #, name, subset, or ID…"
                  autoFocus
                />
                <button
                  type="button"
                  className="hadmin-btn-secondary"
                  disabled={!visibleKeys.length}
                  onClick={() => setManagedVariantItemIds((current) => {
                    const next = new Set(current);
                    visibleKeys.forEach((key) => next.add(key));
                    return next;
                  })}
                >
                  Check Visible
                </button>
                <button
                  type="button"
                  className="hadmin-btn-secondary"
                  disabled={!visibleKeys.length}
                  onClick={() => setManagedVariantItemIds((current) => {
                    const next = new Set(current);
                    visibleKeys.forEach((key) => next.delete(key));
                    return next;
                  })}
                >
                  Uncheck Visible
                </button>
              </div>

              <div className="hrelease-selection-summary" style={{ marginBottom: 12 }}>
                <strong>{applicableCount} applicable</strong>
                <span>{excludedCount} not applicable · {visible.length} shown</span>
              </div>

              <div className="hadmin-table-wrap hrelease-items-table-wrap">
                <table className="hadmin-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={allVisibleChecked}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setManagedVariantItemIds((current) => {
                              const next = new Set(current);
                              visibleKeys.forEach((key) => {
                                if (checked) next.add(key);
                                else next.delete(key);
                              });
                              return next;
                            });
                          }}
                          aria-label="Toggle all visible cards"
                        />
                      </th>
                      <th>ID</th>
                      <th>Card #</th>
                      <th>Name</th>
                      <th>Subset</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((item) => {
                      const key = String(item.id);
                      return (
                        <tr key={item.id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={managedVariantItemIds.has(key)}
                              onChange={() => setManagedVariantItemIds((current) => {
                                const next = new Set(current);
                                if (next.has(key)) next.delete(key);
                                else next.add(key);
                                return next;
                              })}
                            />
                          </td>
                          <td>{item.id}</td>
                          <td>{item.card_number || "—"}</td>
                          <td>{item.name || "Unnamed"}</td>
                          <td>{item.subset || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="hadmin-actions hrelease-create-actions">
                <button type="button" className="hadmin-btn-secondary" onClick={() => setManagingVariantId("")} disabled={busy}>Cancel</button>
                <button type="button" className="hadmin-btn" onClick={saveManagedVariantCards} disabled={busy}>
                  {busy ? "Saving…" : `Save ${applicableCount} Applicable Card${applicableCount === 1 ? "" : "s"}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {editingGroupId && selectedRelease && (() => {
        const group = groups.find((entry) => String(entry.id) === String(editingGroupId));
        const members = groupMembers
          .filter((member) => String(member.group_id) === String(editingGroupId))
          .map((member) => items.find((item) => String(item.id) === String(member.item_id)))
          .filter(Boolean);
        if (!group) return null;
        return (
          <div className="hrelease-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) setEditingGroupId(""); }}>
            <div className="hadmin-panel hrelease-subset-modal" role="dialog" aria-modal="true">
              <div className="hadmin-section-heading">
                <div><h2>{group.name}</h2><p>{members.length} canonical card{members.length === 1 ? "" : "s"} in this managed variant group.</p></div>
                <button type="button" className="hadmin-icon-btn" onClick={() => setEditingGroupId("")} disabled={busy}>×</button>
              </div>
              <div className="hadmin-table-wrap hrelease-items-table-wrap">
                <table className="hadmin-table">
                  <thead><tr><th></th><th>ID</th><th>Card #</th><th>Name</th><th>Subset</th></tr></thead>
                  <tbody>
                    {members.map((item) => {
                      const key = String(item.id);
                      return <tr key={item.id}><td><input type="checkbox" checked={selectedGroupMemberIds.has(key)} onChange={() => setSelectedGroupMemberIds((current) => { const next = new Set(current); if (next.has(key)) next.delete(key); else next.add(key); return next; })} /></td><td>{item.id}</td><td>{item.card_number || "—"}</td><td>{item.name || "Unnamed"}</td><td>{item.subset || "—"}</td></tr>;
                    })}
                  </tbody>
                </table>
              </div>
              <div className="hadmin-info hrelease-create-note">Removing cards narrows applicability. Two or more cards remain a group; one card normalizes to item scope; zero removes the applicability.</div>
              <div className="hadmin-actions hrelease-create-actions">
                <button type="button" className="hadmin-btn-secondary" onClick={() => setEditingGroupId("")} disabled={busy}>Close</button>
                <button type="button" className="hadmin-btn-danger" onClick={removeSelectedGroupMembers} disabled={busy || !selectedGroupMemberIds.size}>{busy ? "Updating…" : `Remove ${selectedGroupMemberIds.size || ""} Selected`}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {showVariantApply && selectedRelease && (
        <div
          className="hrelease-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !busy) {
              setShowVariantApply(false);
            }
          }}
        >
          <div
            className="hadmin-panel hrelease-subset-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hrelease-variant-title"
          >
            <div className="hadmin-section-heading">
              <div>
                <h2 id="hrelease-variant-title">Apply Variant</h2>
                <p>
                  Apply an existing canonical variant to the selected cards in
                  release #{selectedRelease.id}.
                </p>
              </div>
              <button
                type="button"
                className="hadmin-icon-btn"
                onClick={() => setShowVariantApply(false)}
                disabled={busy}
                aria-label="Close apply variant"
              >
                ×
              </button>
            </div>

            <div className="hrelease-selection-summary">
              <strong>
                {selectedItems.length} card{selectedItems.length === 1 ? "" : "s"} selected
              </strong>
              <span>{releaseLabel(selectedRelease)}</span>
            </div>

            <div className="hadmin-form-field">
              <label>Search canonical variants</label>
              <input
                className="hadmin-input"
                value={variantSearch}
                onChange={(event) => {
                  setVariantSearch(event.target.value);
                  setSelectedVariantId("");
                }}
                placeholder="e.g. Piedmont 350 Factory No.25"
                autoFocus
              />
            </div>

            {loadingVariants ? (
              <div className="hadmin-empty">Searching variants…</div>
            ) : clean(variantSearch).length < 2 ? (
              <div className="hadmin-muted hrelease-create-note">
                Type at least 2 characters to search the canonical variant list.
              </div>
            ) : variantOptions.length ? (
              <div className="hadmin-form-field hrelease-field-gap">
                <label>Canonical variant</label>
                <select
                  className="hadmin-select"
                  value={selectedVariantId}
                  onChange={(event) => setSelectedVariantId(event.target.value)}
                >
                  <option value="">Choose variant…</option>
                  {variantOptions.map((variant) => (
                    <option key={variant.id} value={variant.id}>
                      {variant.name}
                      {variant.category ? ` · ${variant.category}` : ""}
                      {` (#${variant.id})`}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="hadmin-empty">No matching canonical variants.</div>
            )}

            <div className="hadmin-info hrelease-create-note">
              This action does not create a new variant. Hobbli stores one-card
              coverage as an item rule; when the same variant applies to multiple
              individual cards, the guarded RPC creates or reuses its managed card
              group automatically.
            </div>

            <div className="hadmin-actions hrelease-create-actions">
              <button
                type="button"
                className="hadmin-btn-secondary"
                onClick={() => setShowVariantApply(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="hadmin-btn"
                onClick={applyVariantToSelectedItems}
                disabled={busy || !selectedVariantId}
              >
                {busy ? "Applying…" : `Apply to ${selectedItems.length} Card${selectedItems.length === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateRelease && (
        <div
          className="hrelease-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !busy) {
              setShowCreateRelease(false);
            }
          }}
        >
          <div
            className="hadmin-panel hrelease-create-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hrelease-create-title"
          >
            <div className="hadmin-section-heading">
              <div>
                <h2 id="hrelease-create-title">Create Release</h2>
                <p>
                  Establish a canonical release before importing or moving cards
                  into it. Duplicate identities are safely reused.
                </p>
              </div>
              <button
                type="button"
                className="hadmin-icon-btn"
                onClick={() => setShowCreateRelease(false)}
                disabled={busy}
                aria-label="Close create release"
              >
                ×
              </button>
            </div>

            <div className="hadmin-form-grid">
              <div className="hadmin-form-field">
                <label>Category</label>
                <select
                  className="hadmin-select"
                  value={createForm.category_id}
                  onChange={(event) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      category_id: event.target.value,
                      subcategory_id: "",
                    }))
                  }
                >
                  <option value="">Choose category…</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="hadmin-form-field">
                <label>Release type</label>
                <select
                  className="hadmin-select"
                  value={createForm.is_multi_sport ? "multi" : "single"}
                  onChange={(event) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      is_multi_sport: event.target.value === "multi",
                      subcategory_id:
                        event.target.value === "multi"
                          ? ""
                          : prev.subcategory_id,
                    }))
                  }
                >
                  <option value="single">Single-sport</option>
                  <option value="multi">Multi-sport</option>
                </select>
              </div>

              <div className="hadmin-form-field">
                <label>Sport / subcategory</label>
                {createForm.is_multi_sport ? (
                  <div className="hrelease-readonly-field">
                    Multiple sports — release subcategory will be NULL
                  </div>
                ) : (
                  <select
                    className="hadmin-select"
                    value={createForm.subcategory_id}
                    onChange={(event) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        subcategory_id: event.target.value,
                      }))
                    }
                  >
                    <option value="">Choose sport…</option>
                    {createSubcategories.map((subcategory) => (
                      <option key={subcategory.id} value={subcategory.id}>
                        {subcategory.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="hadmin-form-field">
                <label>Year</label>
                <input
                  className="hadmin-input"
                  type="number"
                  value={createForm.year}
                  onChange={(event) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      year: event.target.value,
                    }))
                  }
                  placeholder="e.g. 1973"
                />
              </div>

              <div className="hadmin-form-field">
                <label>Year end</label>
                <input
                  className="hadmin-input"
                  type="number"
                  value={createForm.year_end}
                  onChange={(event) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      year_end: event.target.value,
                    }))
                  }
                  placeholder="Optional"
                />
              </div>

              <div className="hadmin-form-field">
                <label>Brand</label>
                <input
                  className="hadmin-input"
                  value={createForm.brand}
                  onChange={(event) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      brand: event.target.value,
                    }))
                  }
                  placeholder="e.g. Topps"
                />
              </div>

              <div className="hadmin-form-field full">
                <label>Set name</label>
                <input
                  className="hadmin-input"
                  value={createForm.set_name}
                  onChange={(event) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      set_name: event.target.value,
                    }))
                  }
                  placeholder="e.g. Sport Kings"
                />
              </div>
            </div>

            <div className="hadmin-panel hrelease-family-picker">
              <div className="hadmin-section-heading"><div><h3>Family</h3><p>Attach this release to the durable product/set family. Reuse the same family for later waves such as Series 2.</p></div><button type="button" className="hadmin-btn-secondary" onClick={() => setShowCreateFamily(true)} disabled={busy}>+ New Family</button></div>
              <input className="hadmin-input" value={familySearch} onChange={(e) => setFamilySearch(e.target.value)} placeholder="Search family key or name…" />
              <select className="hadmin-select hrelease-field-gap" value={createForm.release_family_id} onChange={(e) => setCreateForm((prev) => ({...prev, release_family_id:e.target.value}))}>
                <option value="">Choose family…</option>
                {filteredFamilies.map((family) => <option key={family.id} value={family.id}>{family.year} · {family.brand} · {family.name} · {family.family_key}</option>)}
              </select>
              <label className="hrelease-check-label"><input type="checkbox" checked={Boolean(createForm.display_release_name)} onChange={(e) => setCreateForm((prev)=>({...prev,display_release_name:e.target.checked}))} /> Show release name in user-facing set label (use for Series 1 / Series 2)</label>
            </div>

            <div className="hadmin-info hrelease-create-note">
              Single-sport releases require a sport. Multi-sport releases
              intentionally store no release-level sport; each canonical item
              retains its own sport/subcategory.
            </div>

            <div className="hadmin-actions hrelease-create-actions">
              <button
                type="button"
                className="hadmin-btn-secondary"
                onClick={() => setShowCreateRelease(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="hadmin-btn"
                onClick={createRelease}
                disabled={busy}
              >
                {busy ? "Creating…" : "Create Release"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateFamily && (
        <div className="hrelease-modal-backdrop" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) setShowCreateFamily(false); }}>
          <div className="hadmin-panel hrelease-subset-modal" role="dialog" aria-modal="true">
            <div className="hadmin-section-heading"><div><h2>Create Release Family</h2><p>The family is the durable product/set identity used by future imports and releases.</p></div><button type="button" className="hadmin-icon-btn" onClick={() => setShowCreateFamily(false)} disabled={busy}>×</button></div>
            <div className="hadmin-form-grid">
              <div className="hadmin-form-field full"><label>Family key</label><input className="hadmin-input" value={familyForm.family_key} onChange={(e)=>setFamilyForm((p)=>({...p,family_key:e.target.value}))} placeholder="2027-topps-baseball" /></div>
              <div className="hadmin-form-field full"><label>Family name</label><input className="hadmin-input" value={familyForm.name} onChange={(e)=>setFamilyForm((p)=>({...p,name:e.target.value}))} placeholder="Topps Baseball" /></div>
            </div>
            <div className="hadmin-info hrelease-create-note">Year, brand, category, sport, and multi-sport status are taken from the release form. Use a stable family key: Series 1 and Series 2 share the same key; Tribute uses its own.</div>
            <div className="hadmin-actions hrelease-create-actions"><button type="button" className="hadmin-btn-secondary" onClick={()=>setShowCreateFamily(false)} disabled={busy}>Cancel</button><button type="button" className="hadmin-btn" onClick={createFamily} disabled={busy}>{busy ? "Creating…" : "Create Family"}</button></div>
          </div>
        </div>
      )}

      <div className="hadmin-info hrelease-footnote">
        Release Manager calls the guarded admin RPCs for mutations; it does not
        reproduce release-maintenance logic in React. Signed in as{" "}
        {currentUser?.email || currentUser?.id || "admin"}.
      </div>
    </div>
  );
}
